import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography, makeStyles } from '@material-ui/core/';
import { useDispatch } from 'react-redux';
import {
	addTagToAsset,
	deleteAllTagsFromAsset,
	deleteTagFromAsset,
	fetchAssetDataByUUID,
	linkRelatedProduct,
	unStageProductFromAsset,
} from '../../redux/actions/manageAssetActions';
import AssetArchiveBtn from '../Asset/AssetArchiveBtn';
import AssetJiraLink from '../Asset/AssetJiraLink';
import AssetNameInput from '../Asset/AssetNameInput';
import TagsMultiSearch from '../Tags/TagsMultiSearch';
import ProductInfoPanel from './ProductInfoPanel';
import CopyClipboardField from '../common/CopyClipboardField';
import AssetLiveStatusIndicator from './AssetLiveStatusIndicator';
import PublishedArtifactsWarningDrawer from './PublishedArtifactsWarningDialog';
import { assetHasPublishedArtifacts } from '../../Utils/AssetUtils';
import CreateJiraBtn from '../Asset/CreateJiraBtn';
import { isEmpty, isNil } from 'lodash';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import RefreshIcon from '@material-ui/icons/Refresh';
import { sanitizeTag } from '../../Utils/GlobalUtils';

const useStyles = makeStyles((theme) => ({
	subtitle: {
		color: theme.palette.neutral.n400,
		textTransform: 'uppercase',
	},
	root: {
		flexGrow: 1,
		padding: '1em',
		backgroundColor: '#F5F5F5',
	},
}));
// Field and val components for asset info card.
const FieldTypography = ({ label }) => {
	const classes = useStyles();
	return (
		<Typography className={classes.subtitle} variant="subtitle1" display="initial">
			{label}
		</Typography>
	);
};
export default function AssetInfo({ id, name, status, asset }) {
	const classes = useStyles();
	const dispatch = useDispatch();
	const { product } = asset;
	const [open, setOpen] = useState(false);
	const productSearchOnValChange = (selectedProductObj, asset) => {
		dispatch(linkRelatedProduct(selectedProductObj, asset));
	};
	const handleDrawerBtnClick = () => {
		setOpen(false);
		dispatch(unStageProductFromAsset(asset, product));
	};
	const productSearchOnInputChange = () => {
		// Throw a warning dialog before product is unlinked, if asset has published artifacts.
		if (assetHasPublishedArtifacts(asset)) {
			setOpen(true);
		} else {
			dispatch(unStageProductFromAsset(asset, product));
		}
	};
	const productSearchProps = {
		asset,
		product,
		onValChange: productSearchOnValChange,
		onInputChange: productSearchOnInputChange,
	};
	// Tags Search on change
	const OnValueChange = (reason, changedDetails) => {
		// when clear all button is clicked on the tags search autocomplete. reason will be clear and changed details will be undefined.
		if (reason === 'clear') {
			dispatch(deleteAllTagsFromAsset(asset));
		}
		if (changedDetails && reason) {
			const tag = sanitizeTag(changedDetails.option);
			if (reason === 'select-option' || reason === 'create-option') {
				dispatch(addTagToAsset(tag, asset));
			} else if (reason === 'remove-option') {
				dispatch(deleteTagFromAsset(tag, asset));
			}
		}
	};
	const tagSearchProps = {
		onValueChange: OnValueChange,
		assetTags: asset.tags,
	};
	const drawerProps = {
		open,
		setOpen,
		primaryBtnClick: handleDrawerBtnClick,
	};

	const CreateAndEditJira = () => {
		// show create Jira button when there is no Jira task.
		// when there is a JIRA task just show only edit jira button.
		if (isNil(asset.jiraTask) || isEmpty(asset.jiraTask)) {
			return (
				<Grid container>
					<Grid item xs={6}>
						<AssetJiraLink asset={asset} />
					</Grid>
					<Grid item xs={3}>
						<CreateJiraBtn assetId={asset.id} productId={product?.id} />
					</Grid>
				</Grid>
			);
		} else if (asset.jiraTask === 'Refresh') {
			return (
				<Grid container>
					<Tooltip title="Jira creation in progress" placement="top" arrow={true}>
						<IconButton onClick={() => dispatch(fetchAssetDataByUUID(id))} color="primary" size="small">
							<RefreshIcon /> Refresh
						</IconButton>
					</Tooltip>
				</Grid>
			);
		} else {
			return (
				<Grid container>
					<AssetJiraLink asset={asset} />
				</Grid>
			);
		}
	};
	return (
		<Grid container className={classes.root} spacing={2}>
			<Grid item xs={6}>
				<Grid container alignItems="center" spacing={1}>
					<AssetLiveStatusIndicator asset={asset} />
					<Grid item>
						<AssetArchiveBtn asset={asset} />
					</Grid>
				</Grid>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<AssetNameInput asset={asset} />
					</Grid>
					<Grid item xs={12} sm className={classes.product}>
						<FieldTypography label="Associated product" />
						<ProductInfoPanel {...productSearchProps} />
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={6}>
				<Grid container spacing={1}>
					<Grid item xs={12}>
						<FieldTypography label={'JIRA TICKET'} />
						<CreateAndEditJira />
					</Grid>
					<Grid item xs={12}>
						<FieldTypography label={'ASSET PPID'} />
						<CopyClipboardField fieldName="PPID" val={id} />
					</Grid>
					<Grid item xs={12}>
						<FieldTypography label={'Manage Searchable Tags'} />
						<TagsMultiSearch {...tagSearchProps} />
					</Grid>
				</Grid>
			</Grid>
			{open ? <PublishedArtifactsWarningDrawer {...drawerProps} /> : null}
		</Grid>
	);
}
AssetInfo.propTypes = {
	id: PropTypes.string.isRequired,
	uuidType: PropTypes.string,
	name: PropTypes.string,
	status: PropTypes.string,
	type: PropTypes.string,
	asset: PropTypes.object,
};
AssetInfo.defaultProps = { type: '3D Asset', uuidType: 'ID:' };
